import { motion } from "framer-motion";
import { Anchor, LineChart, GitCompareArrows, Gauge } from "lucide-react";
import { ASSETS } from "../lib/constants";

const SUPPORTING = [
    {
        icon: Anchor,
        t: "Anchored to your baseline",
        d: "A single blood result anchors your personal pharmacokinetics — the model reflects your physiology, not a population average.",
    },
    {
        icon: LineChart,
        t: "See the shape, not just the number",
        d: "Visualize peak-to-trough fluctuation across any protocol — understand how your levels actually behave between injections.",
    },
    {
        icon: GitCompareArrows,
        t: "Compare any protocol",
        d: "Model dose and frequency combinations against your baseline — without the trial-and-error of running them first.",
    },
    {
        icon: Gauge,
        t: "Dynamic target range",
        d: "Red, amber, and green bands on your chart are set from your latest biomarkers. They're not fixed — they adjust to your health.",
    },
];

export default function Testimator() {
    return (
        <section
            id="testimator"
            data-testid="testimator-section"
            className="relative py-24 lg:py-32 bg-black overflow-hidden border-t border-white/5"
        >
            {/* radial wash */}
            <div
                className="absolute -top-32 right-0 w-[700px] h-[700px] rounded-full pointer-events-none"
                style={{
                    background:
                        "radial-gradient(circle, rgba(43,166,224,0.10), transparent 60%)",
                }}
            />

            <div className="relative max-w-7xl mx-auto px-6 lg:px-10">
                {/* Header */}
                <div className="grid lg:grid-cols-2 gap-10 lg:gap-16 mb-14 lg:mb-20 items-end">
                    <div>
                        <div className="eyebrow mb-6">
                            <span className="dot" /> The Testimator™
                        </div>
                        <h2 className="font-display font-bold clamp-h2 text-white">
                            <span className="blue-glow">Dial in</span>
                            <br />
                            before you run it.
                        </h2>
                    </div>
                    <p className="text-white/65 text-base lg:text-lg leading-relaxed lg:pb-2">
                        The Testimator models your individual testosterone
                        response — so you can visualize how protocol adjustments
                        affect your levels before you run them. It's not about
                        hitting a number. It's about understanding the shape of
                        your response.
                    </p>
                </div>

                {/* Phone + supporting tiles */}
                <div className="grid lg:grid-cols-[1fr_1.4fr] gap-10 lg:gap-16 items-start">
                    {/* Phone */}
                    <motion.div
                        initial={{ opacity: 0, y: 30 }}
                        whileInView={{ opacity: 1, y: 0 }}
                        viewport={{ once: true, amount: 0.2 }}
                        transition={{ duration: 0.8 }}
                        className="relative mx-auto lg:mx-0 w-full max-w-[340px] lg:sticky lg:top-28"
                        data-testid="testimator-phone"
                    >
                        <div className="phone-frame">
                            <div className="phone-screen">
                                <img
                                    src={ASSETS.testimatorScreen}
                                    alt="The Testimator screen"
                                    className="absolute inset-0 w-full h-full object-cover object-top"
                                />
                            </div>
                        </div>
                        {/* Floating chip */}
                        <div className="absolute -right-4 top-[78%] hidden lg:flex items-center gap-2 bg-black/85 border border-white/10 backdrop-blur-xl rounded-full px-3 py-2 text-xs shadow-lg">
                            <span className="pulse-dot-blue w-2 h-2 rounded-full bg-[#2ba6e0]" />
                            <span className="text-white/80 font-mono">
                                Predicted T levels
                            </span>
                        </div>
                    </motion.div>

                    {/* Right column */}
                    <div>
                        {/* How it works pull-out */}
                        <div className="tile p-6 lg:p-7 mb-6 relative overflow-hidden">
                            <div
                                className="absolute -top-12 -right-12 w-44 h-44 rounded-full pointer-events-none"
                                style={{
                                    background:
                                        "radial-gradient(circle, rgba(43,166,224,0.18), transparent 60%)",
                                }}
                            />
                            <div className="relative">
                                <div className="text-[11px] uppercase tracking-[0.18em] font-mono text-[#49bef5] mb-3">
                                    How It Works
                                </div>
                                <p className="text-white/75 text-[15px] lg:text-[16px] leading-relaxed">
                                    Anchors your personal pharmacokinetics to a
                                    single baseline blood result — then applies
                                    those parameters to predict how your levels
                                    would behave on any new target protocol.
                                </p>
                            </div>
                        </div>

                        {/* Supporting tiles */}
                        <div className="grid sm:grid-cols-2 gap-4">
                            {SUPPORTING.map((s, i) => (
                                <motion.div
                                    key={s.t}
                                    initial={{ opacity: 0, y: 20 }}
                                    whileInView={{ opacity: 1, y: 0 }}
                                    viewport={{ once: true, amount: 0.2 }}
                                    transition={{ duration: 0.4, delay: i * 0.06 }}
                                    className="tile p-5"
                                    data-testid={`testimator-tile-${s.t.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
                                >
                                    <div className="w-10 h-10 rounded-lg bg-[#2ba6e0]/10 border border-[#2ba6e0]/25 flex items-center justify-center mb-4">
                                        <s.icon
                                            size={18}
                                            className="text-[#49bef5]"
                                        />
                                    </div>
                                    <h3 className="font-display font-semibold text-white text-[16.5px] mb-1.5 leading-tight">
                                        {s.t}
                                    </h3>
                                    <p className="text-sm text-white/55 leading-relaxed">
                                        {s.d}
                                    </p>
                                </motion.div>
                            ))}
                        </div>
                    </div>
                </div>
            </div>
        </section>
    );
}
